import { useState, useCallback, useRef, useEffect, useMemo, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  useWindowDimensions,
  Animated,
  Platform,
  Pressable,
} from 'react-native';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, EvidenceTable } from '../../components';
import { useAuth } from '../../contexts/AuthContext';
import { GoalsSkeleton } from '../../components/Skeleton';
import { useRoleNavigation } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { risksApi, goalsApi } from '../../services/apiService';
import { downloadBlobAsFile } from '../../utils/downloadFile';
import { ExportIcon } from '../../components/Icons';
import { useRisksQuery } from '../../hooks/queries';
import { OrganizationFilterDropdown, useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';
import { queryKeys } from '../../providers/QueryProvider';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { RiskList, StrategicGoalList } from '../../types';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type NavigationProp = NativeStackNavigationProp<any>;

// Icons
const ChevronDownIcon = ({ size = 14, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronLeftIcon = ({ size = 14, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 14, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 14, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 16, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Edit icon (pencil)
const EditIcon = ({ size = 18, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Extended risk interface with related name
interface RiskWithGoal extends RiskList {
  strategic_goal_name?: string;
  strategic_goal_id?: number;
}

// Group risks by strategic goal
interface RiskGroup {
  goalId: number;
  goalName: string;
  organizationName?: string;
  risks: RiskWithGoal[];
}

// Check icon for toast
const CheckIcon = ({ size = 20, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FileInputIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

export default function RisksScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute();
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 768;
  const { isViewOnly, canViewAllOrganizations } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();
  const { user } = useAuth();
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const ownDeptIdForEvidence = user?.profile?.organization;

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
  };

  const [refreshing, setRefreshing] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastOpacity = useRef(new Animated.Value(0)).current;

  // Filters
  const [filterGoal, setFilterGoal] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const goalBtnRef = useRef<any>(null);
  const typeBtnRef = useRef<any>(null);
  const levelBtnRef = useRef<any>(null);

  const openDropdown = (ref: any, setter: (v: boolean) => void) => {
    if (ref.current) {
      ref.current.measureInWindow((x: number, y: number, w: number, h: number) => {
        setDropdownPosition({ top: y + h + 4, left: x, width: w });
        setter(true);
      });
    }
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [isExporting, setIsExporting] = useState(false);

  const queryClient = useQueryClient();

  // Main risks query — React Query renders cached data instantly on focus.
  const {
    data: risksResponse,
    isLoading: loading,
    refetch,
  } = useRisksQuery({
    page: currentPage,
    pageSize,
    risk_type: filterType !== 'all' ? filterType : undefined,
    risk_level: filterLevel !== 'all' ? filterLevel : undefined,
    related_id: filterGoal !== 'all' ? filterGoal : undefined,
    related_type: filterGoal !== 'all' ? 'strategic_goal' : undefined,
    department_id: selectedOrgId ?? undefined,
    year: selectedYear,
  });

  // Goals list (for filter dropdown + grouping) — cached longer, rarely changes.
  const { data: goalsDropdownData } = useQuery({
    queryKey: ['goals', 'dropdown', 'risks'],
    queryFn: () => goalsApi.list({ page_size: 100 }),
    staleTime: 10 * 60 * 1000,
  });
  const goals: StrategicGoalList[] = goalsDropdownData?.results || [];

  // Derive grouped risks from the two queries. Re-runs only when the upstream
  // data changes — no manual state juggling needed.
  const riskGroups: RiskGroup[] = useMemo(() => {
    const risks: RiskWithGoal[] = risksResponse?.results || [];
    const goalMap = new Map<number, RiskGroup>();

    risks.forEach((risk) => {
      if (risk.related_type === 'strategic_goal') {
        const goalId = risk.related_id;
        const goal = goals.find((g) => g.id === goalId);
        const goalName =
          risk.related_name ||
          goal?.title ||
          t('goals.strategicGoalNumber', { id: goalId });

        if (!goalMap.has(goalId)) {
          goalMap.set(goalId, {
            goalId,
            goalName,
            organizationName: risk.organization_name || undefined,
            risks: [],
          });
        }
        goalMap.get(goalId)!.risks.push({
          ...risk,
          strategic_goal_name: goalName,
          strategic_goal_id: goalId,
        });
      }
    });

    return Array.from(goalMap.values());
  }, [risksResponse, goals, t]);

  const totalCount = risksResponse?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Back-compat shim for handlers that called fetchData().
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
  }, [queryClient]);

  const handleExportExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      // Backend builds the risks report, same filters.
      const params: Record<string, any> = { lang: language, year: selectedYear };
      if (selectedOrgId) params.department_id = selectedOrgId;

      const response = await risksApi.exportExcel(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.risks')}.xlsx`);
    } catch (error) {
      console.error('Risks export error:', error);
      setToastMessage(t('messages.exportError'));
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setIsExporting(false);
    }
  }, [selectedOrgId, selectedYear, language, t]);

  // Show toast notification
  const showSuccessToast = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    toastOpacity.setValue(0);

    // useNativeDriver is not supported on web
    const useNativeDriver = Platform.OS !== 'web';

    // Fade in
    Animated.timing(toastOpacity, {
      toValue: 1,
      duration: 300,
      useNativeDriver,
    }).start();

    // Auto hide after 3 seconds
    setTimeout(() => {
      Animated.timing(toastOpacity, {
        toValue: 0,
        duration: 300,
        useNativeDriver,
      }).start(() => {
        setShowToast(false);
      });
    }, 3000);
  }, [toastOpacity]);

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();

      // Check for success message from navigation params
      const params = route.params as { successMessage?: string } | undefined;
      if (params?.successMessage) {
        showSuccessToast(params.successMessage);
        // Clear the param to avoid showing again
        navigation.setParams({ successMessage: undefined });
      }
    }, [refetch, route.params, showSuccessToast, navigation])
  );

  // Reset to page 1 when year changes; query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedOrgId]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const applyFilters = () => {
    setShowGoalDropdown(false);
    setShowTypeDropdown(false);
    setShowLevelDropdown(false);
    // Reset to first page. Filter state changes already flow through the
    // query key so React Query picks them up on the next render.
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterType('all');
    setFilterLevel('all');
    setFilterGoal('all');
    setSelectedOrgId(null);
    setShowGoalDropdown(false);
    setShowTypeDropdown(false);
    setShowLevelDropdown(false);
    // Reset to first page - fetchData will be called due to dependency change
    setCurrentPage(1);
  };

  const hasActiveFilters = filterType !== 'all' || filterLevel !== 'all' || filterGoal !== 'all' || selectedOrgId != null;

  // Risk level badge colors (solid backgrounds with white text per Figma)
  const getRiskLevelBadgeColor = (level: string): string => {
    switch (level) {
      case 'critical': return tokens.colors.raw.red["500"]; // Red
      case 'high': return tokens.colors.raw.red["500"]; // Red
      case 'medium': return '#FF7F0E'; // Orange (Figma chart/legacy/chart-2)
      case 'low': return tokens.colors.raw.green["500"]; // Green
      default: return tokens.colors.raw.gray["400"];
    }
  };

  // Risk type badge color (purple per Figma)
  const getRiskTypeBadgeColor = (): string => {
    return '#9467BD'; // Purple (Figma chart/legacy/chart-5)
  };

  const getRiskTypeLabel = (type: string): string => {
    switch (type) {
      case 'strategic': return t('risks.typeStrategic');
      case 'operational': return t('risks.typeOperational');
      case 'financial': return t('risks.typeFinancial');
      case 'compliance': return t('risks.typeCompliance');
      default: return type;
    }
  };

  const getRiskLevelLabel = (level: string): string => {
    switch (level) {
      case 'critical': return t('risks.levelCritical');
      case 'high': return t('risks.levelHigh');
      case 'medium': return t('risks.levelMedium');
      case 'low': return t('risks.levelLow');
      default: return level;
    }
  };

  // Full-page skeleton only on cold load (no cached data yet). Warm cache
  // and background refetches fall through to the regular render.
  if (loading && riskGroups.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />

      {/* Success Toast Notification */}
      {showToast && (
        <Animated.View style={[styles.toast, { opacity: toastOpacity }]}>
          <CheckIcon size={20} color={tokens.colors.raw.white} />
          <Text style={styles.toastText}>{toastMessage}</Text>
        </Animated.View>
      )}

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('navigation.risks') },
          ]}
        />

        <PageHeader
          title={t('risks.title')}
          actions={
            <View style={{ flexDirection: 'row', gap: tokens.spacing.absolute["2"] }}>
              {!isViewOnly && (
                <Button
                  variant="primary"
                  onPress={() => navigation.navigate('AddRisk')}
                  trailingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}
                >
                  {t('risks.addRisk')}
                </Button>
              )}
              {!isViewOnly && (
                <Button
                  variant="secondary"
                  onPress={() => navigation.navigate('ImportRisks' as any)}
                  trailingIcon={<FileInputIcon size={16} color={themeColors.textPrimary} />}
                >
                  {t('importRisks.title')}
                </Button>
              )}
              <Button
                variant="secondary"
                onPress={handleExportExcel}
                disabled={isExporting}
                trailingIcon={<ExportIcon size={16} color={themeColors.textPrimary} />}
              >
                {t('common.exportExcel')}
              </Button>
            </View>
          }
          contentTrailing={
            <View style={styles.filterRowWrapper}>
          <View style={styles.filterRow}>
          {/* إزالة الفلتر button - only show when filters are active */}
          {hasActiveFilters && (
            <TouchableOpacity style={styles.clearFilterButton} onPress={clearFilters}>
              <XIcon size={14} color={tokens.colors.raw.red["500"]} />
              <Text style={styles.clearFilterText}>{t('filters.clearFilter')}</Text>
            </TouchableOpacity>
          )}

          {/* Apply Filter button */}
          <TouchableOpacity style={[styles.applyFilterBtn, { backgroundColor: themeColors.sectionBg }]} onPress={applyFilters}>
            <Text style={[styles.applyFilterBtnText, { color: themeColors.textPrimary }]}>{t('common.applyFilter')}</Text>
          </TouchableOpacity>

          {/* All Levels dropdown */}
          <View style={styles.filterDropdownContainer} ref={levelBtnRef}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => {
                setShowTypeDropdown(false);
                setShowGoalDropdown(false);
                openDropdown(levelBtnRef, (v) => setShowLevelDropdown(v));
              }}
            >
              <ChevronDownIcon size={14} color={themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: themeColors.textPrimary }]}>
                {filterLevel === 'all' ? t('risks.allLevels') : getRiskLevelLabel(filterLevel)}
              </Text>
            </TouchableOpacity>
          </View>

          {/* All Types dropdown */}
          <View style={styles.filterDropdownContainer} ref={typeBtnRef}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => {
                setShowLevelDropdown(false);
                setShowGoalDropdown(false);
                openDropdown(typeBtnRef, (v) => setShowTypeDropdown(v));
              }}
            >
              <ChevronDownIcon size={14} color={themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: themeColors.textPrimary }]}>
                {filterType === 'all' ? t('risks.allTypes') : getRiskTypeLabel(filterType)}
              </Text>
            </TouchableOpacity>
          </View>

          {/* All Goals dropdown */}
          <View style={styles.filterDropdownContainer} ref={goalBtnRef}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => {
                setShowLevelDropdown(false);
                setShowTypeDropdown(false);
                openDropdown(goalBtnRef, (v) => setShowGoalDropdown(v));
              }}
            >
              <ChevronDownIcon size={14} color={themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                {filterGoal === 'all' ? t('risks.allGoals') : translate(goals.find(g => g.id.toString() === filterGoal)?.title) || filterGoal}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Organization filter - last child = rightmost in RTL = first reading position */}
          {canViewAllOrgs && (
            <View style={styles.filterDropdownContainer}>
              <OrganizationFilterDropdown
                selectedOrgId={selectedOrgId}
                onChange={setSelectedOrgId}
              />
            </View>
          )}
        </View>
            </View>
          }
          style={{ marginBottom: tokens.spacing.absolute["6"] }}
        />

        {/* Dropdown portals - rendered outside all stacking contexts */}
        {Platform.OS === 'web' && (showLevelDropdown || showTypeDropdown || showGoalDropdown) && createPortal(
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => { setShowLevelDropdown(false); setShowTypeDropdown(false); setShowGoalDropdown(false); }} />
            <div style={{ position: 'fixed', top: dropdownPosition.top, insetInlineStart: dropdownPosition.left, width: dropdownPosition.width, zIndex: 9999, backgroundColor: themeColors.card, borderRadius: tokens.radii.absolute["radius-8"], border: `1px solid ${themeColors.cardBorder}`, boxShadow: '0 4px 8px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' as any }}>
              {showLevelDropdown && ['all', 'low', 'medium', 'high', 'critical'].map((level) => (
                <TouchableOpacity key={level} style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterLevel(level); setShowLevelDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterLevel === level && styles.dropdownItemTextActive]}>
                    {level === 'all' ? t('risks.allLevels') : getRiskLevelLabel(level)}
                  </Text>
                </TouchableOpacity>
              ))}
              {showTypeDropdown && ['all', 'strategic', 'operational', 'financial', 'compliance'].map((type) => (
                <TouchableOpacity key={type} style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterType(type); setShowTypeDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterType === type && styles.dropdownItemTextActive]}>
                    {type === 'all' ? t('risks.allTypes') : getRiskTypeLabel(type)}
                  </Text>
                </TouchableOpacity>
              ))}
              {showGoalDropdown && (
                <>
                  <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterGoal('all'); setShowGoalDropdown(false); }}>
                    <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterGoal === 'all' && styles.dropdownItemTextActive]}>{t('risks.allGoals')}</Text>
                  </TouchableOpacity>
                  {goals.map((goal) => (
                    <TouchableOpacity key={goal.id} style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterGoal(goal.id.toString()); setShowGoalDropdown(false); }}>
                      <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterGoal === goal.id.toString() && styles.dropdownItemTextActive]} numberOfLines={1}>{translate(goal.title)}</Text>
                    </TouchableOpacity>
                  ))}
                </>
              )}
            </div>
          </>,
          document.body
        )}

        {/* Risk Groups */}
        {riskGroups.length > 0 ? (
          riskGroups.map((group) => (
            <View key={group.goalId} style={styles.riskGroupSection}>
              {/* Goal Section with Gray Background */}
              <View style={[styles.goalSection, { backgroundColor: themeColors.sectionBg }]}>
                {/* Goal Header */}
                <View style={styles.goalHeader}>
                  {/* Org name badge - shown for strategy view */}
                  {canViewAllOrganizations && group.organizationName && (
                    <View style={styles.orgNameBadge}>
                      <Text style={styles.orgNameBadgeText} numberOfLines={1}>{translateOrg(group.organizationName)}</Text>
                    </View>
                  )}
                  <Text style={[styles.goalTitle, { color: themeColors.textPrimary }]}>{translate(group.goalName)}</Text>
                  <View style={[styles.goalBullet, { backgroundColor: themeColors.textPrimary }]} />
                </View>

                {/* Risk Cards - Horizontal Scroll RTL */}
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.riskCardsScrollContent}
                  style={styles.riskCardsScroll}
                >
                  {/* Cards in RTL order (reverse for RTL scroll) - each card is clickable */}
                  {[...group.risks].reverse().map((risk) => (
                    <TouchableOpacity
                      key={risk.id}
                      style={[styles.riskCard, { width: isSmallScreen ? width - 80 : (width - 340) / 2, backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                      onPress={() => {
                        navigation.navigate('AddRisk', { riskId: risk.id });
                      }}
                      activeOpacity={0.7}
                    >
                      {/* Risk Code */}
                      {risk.code ? (
                        <Text style={[styles.riskCode, { color: themeColors.textPrimary }]}>{risk.code}</Text>
                      ) : null}

                      {/* Risk Name */}
                      <Text style={[styles.riskName, { color: themeColors.textPrimary }]}>{translate(risk.name)}</Text>

                      {/* Badges Row: Classification + Risk Level */}
                      <View style={styles.badgesRow}>
                        {/* Risk Level Badge with Label - Left side */}
                        <View style={styles.badgeContainer}>
                          <View style={[styles.levelBadge, { backgroundColor: getRiskLevelBadgeColor(risk.risk_level) }]}>
                            <Text style={styles.levelBadgeText}>
                              {getRiskLevelLabel(risk.risk_level)}
                            </Text>
                          </View>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.riskLevel')}:</Text>
                        </View>

                        {/* Risk Type Badge with Label - Right side */}
                        <View style={styles.badgeContainer}>
                          <View style={[styles.typeBadge, { backgroundColor: getRiskTypeBadgeColor() }]}>
                            <Text style={styles.typeBadgeText}>{getRiskTypeLabel(risk.risk_type)}</Text>
                          </View>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.classification')}:</Text>
                        </View>
                      </View>

                      {/* Owner + Impact Description Row */}
                      <View style={styles.badgesRow}>
                        {/* Impact Description - Left side */}
                        <View style={styles.impactContainer}>
                          <Text style={[styles.impactText, { color: themeColors.textPrimary }]} numberOfLines={2}>
                            {translate(risk.expected_impact) || '-'}
                          </Text>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.impactDescription')}:</Text>
                        </View>

                        {/* Owner Badge - Right side */}
                        <View style={styles.badgeContainer}>
                          <View style={styles.ownerBadge}>
                            <Text style={styles.ownerBadgeText} numberOfLines={1}>{translate(risk.risk_owner) || '-'}</Text>
                          </View>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.owner')}:</Text>
                        </View>
                      </View>

                      {/* Procedures Section */}
                      <View style={styles.proceduresRow}>
                        {/* Treatment Procedures - Left */}
                        <View style={[styles.procedureColumn, { backgroundColor: themeColors.sectionBg }]}>
                          <Text style={[styles.procedureTitle, { color: themeColors.textPrimary }]}>{t('risks.mitigationActions')}</Text>
                          <Text style={[styles.procedureText, { color: themeColors.textPrimary }]}>
                            • {translate(risk.mitigation_actions) || t('risks.currentProceduresSufficient')}
                          </Text>
                        </View>

                        {/* Current Procedures - Right */}
                        <View style={[styles.procedureColumn, { backgroundColor: themeColors.sectionBg }]}>
                          <Text style={[styles.procedureTitle, { color: themeColors.textPrimary }]}>{t('risks.currentAppliedProcedures')}</Text>
                          <Text style={[styles.procedureText, { color: themeColors.textPrimary }]}>
                            • {translate(risk.internal_causes) || t('risks.currentProceduresDefault')}
                          </Text>
                        </View>
                      </View>

                      {/* Edit Icon Row - Below procedures */}
                      {!isViewOnly && (
                        <View style={styles.editIconRow}>
                          <EditIcon size={18} color={themeColors.iconDefault} />
                        </View>
                      )}
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>
          ))
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>{t('risks.noRisksRegistered')}</Text>
          </View>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <View style={styles.pagination}>
            <View style={[styles.paginationButtons, { flexDirection: rtlRow }]}>
              <TouchableOpacity
                style={[styles.paginationBtn, currentPage === 1 && styles.paginationBtnDisabled]}
                onPress={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <Text style={[styles.paginationBtnText, currentPage === 1 && styles.paginationBtnTextDisabled]}>{t('common.previous')}</Text>
                <ChevronRightIcon size={14} color={currentPage === 1 ? tokens.colors.raw.slate["300"] : tokens.colors.raw.slate["500"]} />
              </TouchableOpacity>

              {/* Page Numbers */}
              {(() => {
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                const end = Math.min(totalPages, start + maxVisible - 1);
                if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                return Array.from({ length: end - start + 1 }, (_, i) => start + i);
              })().map((page) => (
                <TouchableOpacity
                  key={page}
                  style={[styles.paginationPageBtn, currentPage === page && styles.paginationPageBtnActive]}
                  onPress={() => setCurrentPage(page)}
                >
                  <Text style={[styles.paginationPageText, currentPage === page && styles.paginationPageTextActive]}>
                    {page}
                  </Text>
                </TouchableOpacity>
              ))}

              <TouchableOpacity
                style={[styles.paginationBtn, currentPage === totalPages && styles.paginationBtnDisabled]}
                onPress={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronLeftIcon size={14} color={currentPage === totalPages ? tokens.colors.raw.slate["300"] : tokens.colors.raw.slate["500"]} />
                <Text style={[styles.paginationBtnText, currentPage === totalPages && styles.paginationBtnTextDisabled]}>{t('common.next')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
        <EvidenceTable
          relatedType="risk_collection"
          filterRelatedId={canViewAllOrgs ? undefined : (ownDeptIdForEvidence ?? undefined)}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence && !!ownDeptIdForEvidence}
          showOrgColumn={canViewAllOrgs}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'risk_collection', relatedId: ownDeptIdForEvidence as number, contextLabel: t('risks.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id, relatedType: ev.related_type, relatedId: ev.related_id, contextLabel: t('risks.title'),
          })}
        />
      </ScrollView>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  // Toast Notification
  toast: {
    position: 'absolute',
    top: 80,
    start: '50%',
    transform: [{ translateX: -150 }],
    width: 300,
    backgroundColor: tokens.colors.raw.green["500"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    zIndex: 1000,
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 10,
  },
  toastText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#737373',
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#D4D4D4',
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },
  filterRowWrapper: {
    zIndex: 100,
    position: 'relative' as any,
  },
  // Filter Row
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["4"],
    marginTop: tokens.spacing.absolute["4"],
    position: 'relative',
    zIndex: 1000,
  },
  applyFilterBtn: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.slate["100"],
  },
  applyFilterBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },
  clearFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#FCA5A5',
    backgroundColor: tokens.colors.raw.red["50"],
    minHeight: 44,
    gap: tokens.spacing.absolute["1_5"],
  },
  clearFilterText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },
  filterDropdownContainer: {
    flex: 1,
    maxWidth: 200,
    position: 'relative',
    overflow: 'visible' as any,
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
  },
  filterDropdownText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownOverlay: {
    position: 'fixed' as any,
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    zIndex: 9998,
  },
  dropdownMenuFixed: {
    position: 'fixed' as any,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 9999,
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownItemTextActive: {
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  // Risk Group Section
  riskGroupSection: {
    marginBottom: tokens.spacing.absolute["6"],
    position: 'relative' as any,
    zIndex: 1,
  },
  goalSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  goalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  goalBullet: {
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.semantic.general.foreground,
  },
  orgNameBadge: {
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  orgNameBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.white,
    textAlign: 'center',
  },
  goalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  // Risk Cards - Horizontal Scroll
  riskCardsScroll: {
    flexDirection: 'row-reverse', // RTL scroll direction
  },
  riskCardsScrollContent: {
    flexDirection: 'row-reverse', // RTL content direction
    paddingHorizontal: tokens.spacing.absolute["0"],
    gap: tokens.spacing.absolute["5"],
  },
  riskCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
    position: 'relative',
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    minWidth: 300,
    maxWidth: 500,
    overflow: 'hidden',
  },
  riskCode: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  riskName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["4"],
  },
  badgeContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
  },
  badgeLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  typeBadge: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    minHeight: 24,
    justifyContent: 'center',
  },
  typeBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["50"],
    textAlign: 'center',
  },
  levelBadge: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    minHeight: 24,
    justifyContent: 'center',
  },
  levelBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["50"],
    textAlign: 'center',
  },
  // Owner badge (gold pill)
  ownerBadge: {
    flex: 1,
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  ownerBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.white,
    textAlign: 'center',
  },
  // Impact description
  impactContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  impactText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  // Procedures
  proceduresRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
  },
  procedureColumn: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    gap: tokens.spacing.absolute["1"],
  },
  procedureTitle: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    height: 20,
    lineHeight: 20,
  },
  procedureText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  // Edit Icon Row
  editIconRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingTop: tokens.spacing.absolute["1"],
  },
  // Empty State
  emptyContainer: {
    padding: tokens.spacing.absolute["10"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: 'center',
  },
  // Pagination
  pagination: {
    marginTop: tokens.spacing.absolute["6"],
    alignItems: 'center',
  },
  paginationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  paginationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  paginationBtnDisabled: {
    opacity: 0.5,
  },
  paginationBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.slate["700"],
  },
  paginationBtnTextDisabled: {
    color: tokens.colors.raw.slate["300"],
  },
  paginationPageBtn: {
    minWidth: 36,
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    backgroundColor: tokens.colors.raw.white,
    alignItems: 'center',
  },
  paginationPageBtnActive: {
    borderColor: tokens.colors.raw.slate["700"],
    backgroundColor: tokens.colors.raw.white,
  },
  paginationPageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.slate["700"],
  },
  paginationPageTextActive: {
    color: tokens.colors.raw.slate["700"],
  },
});

const styles = getStyles(true);
